import React, { useMemo } from 'react'

type Props = {
  labels: string[]
  values: number[]
  max?: number
  target?: number
  className?: string
}

function wrapLabel(label: string, maxLen = 18): string[] {
  const words = label.split(/\s+/)
  const lines: string[] = []
  let cur = ''
  words.forEach((w) => {
    const next = (cur + ' ' + w).trim()
    if (next.length > maxLen && cur) {
      lines.push(cur)
      cur = w
    } else {
      cur = next
    }
  })
  if (cur) lines.push(cur)
  return lines.length ? lines : [label]
}

/** SVG radar matching the maturity-analysis redesign (rings, target dash, fill). */
export default function MaturityRadar({
  labels,
  values,
  max = 5,
  target = 3,
  className = '',
}: Props) {
  const n = Math.max(labels.length, 1)
  const cx = 250
  const cy = 225
  const R = 150
  const rings = [1, 2, 3, 4, 5]

  const pt = (i: number, r: number): [number, number] => {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / n
    return [cx + Math.cos(a) * r, cy + Math.sin(a) * r]
  }

  const ringPolys = useMemo(
    () =>
      rings.map((v) => {
        const r = (R * v) / max
        const pts = Array.from({ length: n }, (_, i) => {
          const [x, y] = pt(i, r)
          return `${x.toFixed(1)},${y.toFixed(1)}`
        }).join(' ')
        return { v, r, pts }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [n, max]
  )

  const targetPts = useMemo(() => {
    const r = (R * target) / max
    return Array.from({ length: n }, (_, i) => {
      const [x, y] = pt(i, r)
      return `${x.toFixed(1)},${y.toFixed(1)}`
    }).join(' ')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [n, max, target])

  const valuePts = useMemo(() => {
    return Array.from({ length: n }, (_, i) => {
      const val = Math.min(max, Math.max(0, values[i] ?? 0))
      const [x, y] = pt(i, (R * val) / max)
      return { x, y, s: `${x.toFixed(1)},${y.toFixed(1)}` }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [n, max, values])

  const valuePoly = valuePts.map((p) => p.s).join(' ')

  return (
    <svg
      viewBox="0 0 500 470"
      className={className}
      style={{ width: '100%', maxWidth: 640, height: 'auto', display: 'block', margin: '0 auto' }}
      role="img"
      aria-label="Maturity radar"
    >
      {ringPolys.map(({ v, r, pts }) => (
        <g key={v}>
          <polygon
            points={pts}
            fill="none"
            stroke="#E4EBEB"
            strokeWidth={1}
          />
          <text
            x={cx + 4}
            y={cy - r + 4}
            fontSize={9}
            fill="#8A9BA7"
            fontFamily="IBM Plex Mono, ui-monospace, monospace"
          >
            {v.toFixed(1)}
          </text>
        </g>
      ))}

      {Array.from({ length: n }, (_, i) => {
        const [x, y] = pt(i, R)
        return (
          <line
            key={`axis-${i}`}
            x1={cx}
            y1={cy}
            x2={x}
            y2={y}
            stroke="#EAF0F0"
          />
        )
      })}

      <polygon
        points={targetPts}
        fill="none"
        stroke="#B0730F"
        strokeWidth={1.3}
        strokeDasharray="4 4"
      />

      <polygon
        points={valuePoly}
        fill="rgba(13,155,135,.18)"
        stroke="#0D9B87"
        strokeWidth={2}
      />

      {valuePts.map((p, i) => (
        <circle key={`pt-${i}`} cx={p.x} cy={p.y} r={3.2} fill="#0D9B87" />
      ))}

      {labels.map((label, i) => {
        const [x, y] = pt(i, R + 24)
        const anchor =
          Math.abs(x - cx) < 12 ? 'middle' : x > cx ? 'start' : 'end'
        const lines = wrapLabel(label)
        return (
          <text
            key={`lbl-${i}`}
            x={x}
            y={y}
            textAnchor={anchor}
            fontSize={9.5}
            fill="#5F7484"
            fontFamily="Lato, sans-serif"
          >
            {lines.map((ln, k) => (
              <tspan key={k} x={x} dy={k === 0 ? 0 : 11}>
                {ln}
              </tspan>
            ))}
          </text>
        )
      })}
    </svg>
  )
}
